import { useEffect, useRef, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import {
    color,
    elevation,
    presenceLayerStyle,
    radius,
    space,
    useOverlayMotion,
} from "@tokens/index";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { motionStyle } from "../motionStyle";
import { Text } from "../Text";
import { TOAST_Z_INDEX } from "./zIndex";

/**
 * Toast: one transient status message at the bottom center.
 *
 * `role="status"` with `aria-live="polite"`. Dismisses on the close control,
 * on the action, or after `autoHideMs` (default 6000). Owns its
 * `AnimatePresence`. Message, tone, and action are retained for the exit
 * animation so a caller that clears them in the same render as `open={false}`
 * does not leave an empty bubble fading out.
 */

export type ToastTone = "info" | "success" | "warning" | "danger";

export interface ToastAction {
    readonly label: string;
    readonly onClick: () => void;
}

export interface ToastProps {
    open: boolean;
    message: ReactNode;
    tone?: ToastTone;
    action?: ToastAction;
    onDismiss: () => void;
    /** Default `6000`. Set `0` to disable the timer. */
    autoHideMs?: number;
    testId?: string;
}

interface ToastSnapshot {
    readonly message: ReactNode;
    readonly tone: ToastTone;
    readonly action: ToastAction | undefined;
}

const toneFg: Record<ToastTone, string> = {
    info: color.status.infoFg,
    success: color.status.successFg,
    warning: color.status.warningFg,
    danger: color.status.dangerFg,
};

const regionStyle: CSSProperties = {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: `calc(${space[5]} + env(safe-area-inset-bottom, 0px))`,
    zIndex: TOAST_Z_INDEX,
    display: "flex",
    justifyContent: "center",
    padding: `0 ${space[3]}`,
    pointerEvents: "none",
};

const cardStyle: CSSProperties = {
    pointerEvents: "auto",
    display: "flex",
    alignItems: "center",
    gap: space[3],
    width: "100%",
    maxWidth: "min(92vw, 520px)",
    minHeight: 48,
    padding: `${space[2]} ${space[2]} ${space[2]} ${space[4]}`,
    background: color.bg.surface2,
    color: color.fg.default,
    border: `1px solid ${elevation[4].border}`,
    borderRadius: radius.lg,
    boxShadow: elevation[4].shadow,
    // The card holds its compositor layer for its lifetime; see `presenceLayerStyle`.
    ...presenceLayerStyle,
};

export function Toast({
    open,
    message,
    tone = "info",
    action,
    onDismiss,
    autoHideMs = 6000,
    testId,
}: ToastProps): ReactElement | null {
    const overlayMotion = useOverlayMotion();
    const dismissRef = useRef(onDismiss);
    dismissRef.current = onDismiss;
    const snapshotRef = useRef<ToastSnapshot>({ message, tone, action });
    if (open) {
        snapshotRef.current = { message, tone, action };
    }
    const shown = snapshotRef.current;

    // The timer restarts when the toast opens or its message changes.
    useEffect(() => {
        if (!open || autoHideMs <= 0) return;
        const id = window.setTimeout(() => dismissRef.current(), autoHideMs);
        return () => window.clearTimeout(id);
    }, [open, autoHideMs, message]);

    if (typeof document === "undefined") return null;

    return createPortal(
        <div style={regionStyle}>
            <AnimatePresence>
                {open && (
                    <motion.div
                        key="toast"
                        role="status"
                        aria-live="polite"
                        data-tone={shown.tone}
                        data-testid={testId}
                        style={motionStyle(cardStyle)}
                        initial={overlayMotion.initial}
                        animate={overlayMotion.animate}
                        exit={overlayMotion.exit}
                        transition={overlayMotion.transition}
                    >
                        <span
                            aria-hidden="true"
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: radius.full,
                                background: toneFg[shown.tone],
                                flexShrink: 0,
                            }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {typeof shown.message === "string" ? (
                                <Text role="body">{shown.message}</Text>
                            ) : (
                                shown.message
                            )}
                        </div>
                        {shown.action !== undefined && (
                            <Button variant="ghost" size="sm" onClick={shown.action.onClick}>
                                {shown.action.label}
                            </Button>
                        )}
                        <IconButton
                            icon={X}
                            aria-label="Dismiss"
                            variant="ghost"
                            size="sm"
                            onClick={onDismiss}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>,
        document.body,
    );
}
